import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Table,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconPill, IconPlus, IconSearch, IconTrash } from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import {
  createMedication,
  deleteMedication,
  listMedications,
  type MedicationCreatePayload,
} from "@/lib/api/references_full";
import { useAuthStore } from "@/stores/auth";

export function MedicationsPage() {
  const { t } = useTranslation();
  const canEdit = useAuthStore((s) => s.hasPermission("reference.update"));
  const [q, setQ] = useState("");
  const [debQ] = useDebouncedValue(q, 300);
  const [opened, { open, close }] = useDisclosure(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["medications", debQ],
    queryFn: () => listMedications({ q: debQ || undefined, limit: 100 }),
  });

  const form = useForm<MedicationCreatePayload>({
    initialValues: { name: "", atc_code: "", dose_form: "", strengths: [], notes: "" },
  });

  const createMutation = useMutation({
    mutationFn: createMedication,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["medications"] });
      notifications.show({ color: "green", message: t("references.med.created") });
      form.reset();
      close();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMedication,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["medications"] }),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("references.medications")}
        subtitle={data ? `${data.total} ${t("references.med.name")}` : undefined}
        back
        actions={
          canEdit && (
            <Button leftSection={<IconPlus size={16} strokeWidth={1.8} />} onClick={open} radius="xl">
              {t("references.med.create")}
            </Button>
          )
        }
      />

      <Card padding="md">
        <TextInput
          rightSection={<IconSearch size={18} strokeWidth={1.8} />}
          placeholder={t("references.med.search")}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0}>
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("references.med.name")}</Table.Th>
                <Table.Th>{t("references.med.atc")}</Table.Th>
                <Table.Th>{t("references.med.dose_form")}</Table.Th>
                <Table.Th>{t("references.med.strengths")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <EmptyState
                      icon={<IconPill size={32} strokeWidth={1.5} />}
                      title={t("references.med.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((m) => (
                <Table.Tr key={m.id}>
                  <Table.Td>
                    <Text fw={600} size="sm">
                      {m.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {m.atc_code ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{m.dose_form ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {m.strengths.map((s) => (
                        <Badge key={s} variant="light" color="blue" size="sm" radius="sm">
                          {s}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {canEdit && m.is_active && (
                      <Tooltip label={t("common.delete")}>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => deleteMutation.mutate(m.id)}
                        >
                          <IconTrash size={16} strokeWidth={1.8} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal opened={opened} onClose={close} title={t("references.med.create")} size="md">
        <form onSubmit={form.onSubmit((v) => createMutation.mutate(v))}>
          <Stack>
            <TextInput
              label={t("references.med.name")}
              placeholder="Paracetamol"
              required
              {...form.getInputProps("name")}
            />
            <TextInput
              label={t("references.med.atc")}
              placeholder={t("placeholder.atc")}
              {...form.getInputProps("atc_code")}
            />
            <TextInput
              label={t("references.med.dose_form")}
              placeholder={t("placeholder.dose_form")}
              {...form.getInputProps("dose_form")}
            />
            <TagsInput
              label={t("references.med.strengths")}
              placeholder={t("placeholder.strengths")}
              {...form.getInputProps("strengths")}
            />
            <Textarea
              label={t("references.med.notes")}
              placeholder={t("placeholder.notes")}
              {...form.getInputProps("notes")}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" color="gray" onClick={close}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                {t("common.create")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
